import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import Fuse from 'fuse.js';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUpdateBlobMutation } from '@/services/api/blobService';
import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

import { treeStyles, TreeViewSize } from '../constants/treeStyles';
import { useDocumentTree } from '../context/DocumentTreeContext';
import { useVirtualizedTree } from '../hooks/useVirtualizedTree';
import { FileNodeItem } from './FileNodeItem';
import { FolderNodeItem } from './FolderNodeItem';
import { SortConfig } from './SortButton';

interface TreeViewProps {
  rootNode: CollectionNode;
  blobs: BlobType[] | undefined;
  searchQuery: string;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, targetCollectionId?: string) => void;
  isDragging: boolean;
  onSuccess?: () => void;
  fuse: Fuse<BlobType> | null;
  size?: TreeViewSize;
  matterId?: string;
  sortConfig?: SortConfig;
}

export const TreeView = ({
  rootNode,
  blobs,
  searchQuery,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  isDragging,
  onSuccess,
  fuse,
  size = 'sm',
  matterId,
  sortConfig = { field: 'default', direction: 'asc' },
}: TreeViewProps) => {
  const {
    expandedFolders,
    activeNodeId,
    toggleFolder,
    setActiveNodeIdAndClearFiles,
    setActiveFileAndSelect,
    setActiveNodeId,
    selectAllInFolder,
    selectAllFiles,
    toggleFileSelection,
    isFolderFullySelected,
    selectedFileIds,
    isTextInputActive,
    selectedFolderIds,
    toggleFolderSelection,
  } = useDocumentTree();

  // Track last clicked file ID for shift-select range (store ID, not index, since tree can change)
  const lastClickedFileIdRef = useRef<string | null>(null);

  const [updateBlob] = useUpdateBlobMutation();

  const parentRef = useRef<HTMLDivElement>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );
  const [draggingFileIds, setDraggingFileIds] = useState<string[]>([]);
  const [draggingFile, setDraggingFile] = useState<BlobType | null>(null);

  // Track the collection ID being targeted during drag
  const [dragTargetId, setDragTargetId] = useState<string | null>(null);
  const [dragTargetParentId, setDragTargetParentId] = useState<string | null>(
    null,
  );

  // Use our custom hook to process the tree data
  const { flattenedTree, findParentCollection } = useVirtualizedTree(
    rootNode,
    blobs,
    searchQuery,
    expandedFolders,
    activeNodeId,
    isFolderFullySelected,
    fuse,
    sortConfig,
  );

  // Handler for file selection that supports shift-click range selection
  const handleFileSelect = useCallback(
    (fileId: string, e?: React.MouseEvent) => {
      if (e?.shiftKey && lastClickedFileIdRef.current !== null) {
        // Shift-click: select range of files between last clicked and current
        // Look up current indices by ID to handle tree structure changes
        const anchorIndex = flattenedTree.findIndex(
          (node) => node.id === lastClickedFileIdRef.current,
        );
        const currentIndex = flattenedTree.findIndex(
          (node) => node.id === fileId,
        );

        if (anchorIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(anchorIndex, currentIndex);
          const end = Math.max(anchorIndex, currentIndex);
          const rangeFileIds = flattenedTree
            .slice(start, end + 1)
            .filter((node) => node.type === 'file')
            .map((node) => node.id);

          // Add range to existing selection
          const newSelection = new Set(selectedFileIds);
          rangeFileIds.forEach((id) => newSelection.add(id));
          selectAllFiles(Array.from(newSelection));
          setActiveNodeId(fileId);
          // Don't update lastClickedFileIdRef on shift-click so user can extend range
          return;
        }
      }

      if (e?.metaKey || e?.ctrlKey) {
        // Cmd/Ctrl-click: toggle individual file in selection
        toggleFileSelection(fileId);
        setActiveNodeId(fileId);
        lastClickedFileIdRef.current = fileId;
        return;
      }

      // Normal click
      if (selectedFileIds.size > 1) {
        // Preserve multi-selection, just change which file is "active" for preview
        setActiveNodeId(fileId);
      } else {
        // Single or no selection - standard behavior
        setActiveFileAndSelect(fileId);
      }
      lastClickedFileIdRef.current = fileId;
    },
    [
      flattenedTree,
      selectedFileIds,
      selectAllFiles,
      setActiveNodeId,
      toggleFileSelection,
      setActiveFileAndSelect,
    ],
  );

  // Setup TanStack virtualizer with row height based on size
  const rowHeight = treeStyles.row[size].height;
  // Add bottom padding to avoid cut-off
  const bottomPadding = 100; // Adjust this value as needed

  const virtualizer = useVirtualizer({
    count: flattenedTree.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
  });

  // Create a wrapper function that prevents toggling the root node
  const handleToggleFolder = (id: string) => {
    if (id !== rootNode.id) {
      toggleFolder(id);
    }
    // If it's the root node, we ensure it's always expanded by adding it to the set
    else if (!expandedFolders.has(rootNode.id)) {
      toggleFolder(id); // Only expand if not already expanded
    }
  };

  // Ensure root node is always expanded on initial render
  useEffect(() => {
    if (rootNode && !expandedFolders.has(rootNode.id)) {
      toggleFolder(rootNode.id);
    }
  }, [rootNode, expandedFolders, toggleFolder]);

  const handleSelectAll = (
    id: string,
    e: React.MouseEvent,
    node: CollectionNode,
  ) => {
    e.stopPropagation();
    selectAllInFolder(id, blobs, node);
  };

  const handleNodeSuccess = () => {
    if (onSuccess) onSuccess();
  };

  const blobMap = useMemo(() => {
    const map = new Map<string, BlobType>();
    blobs?.forEach((blob) => map.set(blob.id, blob));
    return map;
  }, [blobs]);
  const isInternalDragging = draggingFileIds.length > 0;
  const combinedDragging = isDragging || isInternalDragging;

  // Enhanced drag and drop handling
  const handleTreeDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    // Find the target collection ID
    const folderElement = (e.target as HTMLElement).closest(
      '[data-collection-id]',
    );
    if (folderElement) {
      const collectionId = folderElement.getAttribute('data-collection-id');
      if (collectionId) {
        setDragTargetId(collectionId);
        setDragTargetParentId(null);
      }
    } else {
      // If not on a folder, check if on a file
      const fileElement = (e.target as HTMLElement).closest(
        '[data-blob-parent-id]',
      );
      if (fileElement) {
        const parentId = fileElement.getAttribute('data-blob-parent-id');
        if (parentId) {
          setDragTargetParentId(parentId);
          setDragTargetId(null);
        }
      } else {
        // If not on a specific target, set root node as the target
        setDragTargetId(rootNode.id);
        setDragTargetParentId(null);
      }
    }

    handleDragOver(e);
  };

  const handleTreeDragLeave = (e: React.DragEvent) => {
    e.preventDefault();

    // Check if we're leaving the entire tree view
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!parentRef.current?.contains(relatedTarget)) {
      setDragTargetId(null);
      setDragTargetParentId(null);
    }

    handleDragLeave(e);
  };

  const handleTreeDrop = (e: React.DragEvent) => {
    e.preventDefault();

    // Find the target collection ID
    let targetCollectionId: string | undefined;

    // Check if dropped on a folder
    const folderElement = (e.target as HTMLElement).closest(
      '[data-collection-id]',
    );
    if (folderElement) {
      targetCollectionId =
        folderElement.getAttribute('data-collection-id') || undefined;
    }

    // If not dropped on a folder, check if dropped on a file and get its parent collection
    if (!targetCollectionId) {
      const fileElement = (e.target as HTMLElement).closest(
        '[data-blob-parent-id]',
      );
      if (fileElement) {
        targetCollectionId =
          fileElement.getAttribute('data-blob-parent-id') || undefined;
      }
    }

    // If not dropped on any specific element, use the root node as target
    if (!targetCollectionId) {
      targetCollectionId = rootNode.id;
    }

    // Reset target highlighting
    setDragTargetId(null);
    setDragTargetParentId(null);

    // Call the handler with the identified target
    handleDrop(e, targetCollectionId);
  };

  const handleInternalDragStart = (event: DragStartEvent) => {
    const fileId = event.active.data.current?.fileId as string | undefined;
    if (!fileId) {
      return;
    }

    const dragIds =
      selectedFileIds.size > 0 && selectedFileIds.has(fileId)
        ? Array.from(selectedFileIds)
        : [fileId];

    setDraggingFileIds(dragIds);
    setDraggingFile(blobMap.get(fileId) ?? null);
  };

  const handleInternalDragEnd = async (event: DragEndEvent) => {
    const folderId =
      event.over?.data.current?.folderId ||
      (event.over?.data.current?.fileParentId as string | undefined);

    if (folderId && draggingFileIds.length > 0) {
      await Promise.all(
        draggingFileIds.map((blobId) => {
          const blob = blobMap.get(blobId);
          if (!blob || blob.collection_details.id === folderId) {
            return Promise.resolve();
          }

          return updateBlob({
            matterId: rootNode.matter_id,
            blobId,
            blob: {
              id: blobId,
              collection: folderId,
            },
          }).unwrap();
        }),
      );

      if (onSuccess) {
        onSuccess();
      }
    }

    setDraggingFileIds([]);
    setDraggingFile(null);
  };

  const handleInternalDragCancel = () => {
    setDraggingFileIds([]);
    setDraggingFile(null);
  };

  // Get the virtual items to render
  const virtualItems = virtualizer.getVirtualItems();

  // Get target folder name for tooltip
  const getTargetFolderName = (): string => {
    if (dragTargetId) {
      // If it's the root folder
      if (dragTargetId === rootNode.id) {
        return 'All Files';
      }

      // Find the folder in the flattened tree
      const targetNode = flattenedTree.find(
        (node) => node.type === 'folder' && node.id === dragTargetId,
      );
      if (targetNode) {
        return (targetNode.data as CollectionNode).name;
      }
    } else if (dragTargetParentId) {
      // Find the parent folder of the file
      const parentCollection = findParentCollection(dragTargetParentId);
      if (parentCollection) {
        return parentCollection.name === 'root'
          ? 'All Files'
          : parentCollection.name;
      }
    }

    return 'this folder';
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleInternalDragStart}
      onDragEnd={handleInternalDragEnd}
      onDragCancel={handleInternalDragCancel}
    >
      <RootDroppableContainer
        rootNodeId={rootNode.id}
        parentRef={parentRef}
        className={`${treeStyles.container.width} border-r border-border overflow-y-auto h-full relative w-full pr-4`}
        onDragOver={handleTreeDragOver}
        onDragLeave={handleTreeDragLeave}
        onDrop={handleTreeDrop}
      >
        {/* Drop zone overlay - shows when dragging files over the tree */}
        {isDragging && (
          <div className="absolute inset-0 z-20 pointer-events-none">
            {/* Semi-transparent blue overlay */}
            <div className="absolute inset-1 bg-blue-500/10 border-2 border-dashed border-blue-400/60 rounded-lg transition-all duration-200" />
            {/* Centered drop indicator */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-blue-500 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Drop files to upload
              </div>
            </div>
          </div>
        )}

        {/* Target tooltip - shows specific folder when hovering */}
        {combinedDragging &&
          !isDragging &&
          (dragTargetId || dragTargetParentId) && (
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded z-10 pointer-events-none shadow-md whitespace-nowrap">
              Drop to upload to &ldquo;{getTargetFolderName()}&rdquo;
            </div>
          )}

        {/* Total size container for virtualizer */}
        <div
          className="relative w-full"
          style={{ height: `${virtualizer.getTotalSize() + bottomPadding}px` }}
        >
          {virtualItems.map((virtualRow) => {
            const node = flattenedTree[virtualRow.index];
            const isTargeted =
              node.type === 'folder'
                ? dragTargetId === node.id
                : dragTargetParentId ===
                  (node.data as BlobType).collection_details.id;

            return (
              <div
                key={node.id}
                className="absolute top-0 left-0 w-full"
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {node.type === 'folder' ? (
                  <DroppableFolderNodeItem
                    node={node.data as CollectionNode}
                    isSelected={node.id === activeNodeId}
                    level={node.level}
                    isExpanded={!!node.isExpanded}
                    hasContent={!!node.hasContent}
                    totalFilesCount={node.totalFilesCount || 0}
                    isAllSelected={!!node.isAllSelected}
                    isRoot={!!node.isRoot}
                    rootNodeId={rootNode.id}
                    onSuccess={onSuccess}
                    onSelect={() => setActiveNodeIdAndClearFiles(node.id)}
                    onToggleExpand={() => handleToggleFolder(node.id)}
                    onSelectAll={(e) =>
                      handleSelectAll(node.id, e, node.data as CollectionNode)
                    }
                    isFolderSelected={selectedFolderIds.has(node.id)}
                    onToggleFolderSelection={() =>
                      toggleFolderSelection(node.id)
                    }
                    matchesSearch={!!node.matchesSearch}
                    isTargeted={isTargeted}
                    isDraggingActive={combinedDragging}
                    size={size}
                    isInternalDragging={isInternalDragging}
                  />
                ) : (
                  <DraggableFileNodeItem
                    blob={node.data as BlobType}
                    level={node.level}
                    collectionTree={
                      findParentCollection(
                        (node.data as BlobType).collection_details.id,
                      ) || rootNode
                    }
                    onSuccess={handleNodeSuccess}
                    onSelect={(e) =>
                      handleFileSelect(node.id, e)
                    }
                    isSelected={node.id === activeNodeId}
                    matchesSearch={!!node.matchesSearch}
                    isTargeted={isTargeted}
                    isDraggingActive={combinedDragging}
                    size={size}
                    matterId={matterId}
                    disableDrag={isTextInputActive}
                  />
                )}
              </div>
            );
          })}

          {/* Bottom padding element */}
          <div style={{ height: `${bottomPadding}px` }} />
        </div>

        {flattenedTree.length === 0 && searchQuery && (
          <div className="flex flex-col items-center justify-center py-4 text-center px-3">
            <p className="text-xs font-medium">No files found</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Try adjusting your search query
            </p>
          </div>
        )}
      </RootDroppableContainer>
      <DragOverlay dropAnimation={null}>
        {draggingFile && (
          <DocumentDragPreview
            blob={draggingFile}
            count={draggingFileIds.length}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default TreeView;

interface DroppableFolderNodeItemProps {
  node: CollectionNode;
  level: number;
  isExpanded: boolean;
  hasContent: boolean;
  totalFilesCount: number;
  isAllSelected: boolean;
  isSelected: boolean;
  isRoot: boolean;
  rootNodeId: string;
  onSuccess?: () => void;
  onSelect: () => void;
  onToggleExpand: () => void;
  onSelectAll: (e: React.MouseEvent) => void;
  isFolderSelected?: boolean;
  onToggleFolderSelection?: () => void;
  matchesSearch: boolean;
  isTargeted: boolean;
  isDraggingActive: boolean;
  size: TreeViewSize;
  isInternalDragging: boolean;
}

const DroppableFolderNodeItem = ({
  node,
  level,
  isExpanded,
  hasContent,
  totalFilesCount,
  isAllSelected,
  isSelected,
  isRoot,
  rootNodeId,
  onSuccess,
  onSelect,
  onToggleExpand,
  onSelectAll,
  isFolderSelected,
  onToggleFolderSelection,
  matchesSearch,
  isTargeted,
  isDraggingActive,
  size,
  isInternalDragging,
}: DroppableFolderNodeItemProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: node.id,
    data: { type: 'folder', folderId: node.id },
  });

  return (
    <div ref={setNodeRef}>
      <FolderNodeItem
        item={node}
        isSelected={isSelected}
        level={level}
        isExpanded={isExpanded}
        hasContent={hasContent}
        totalFilesCount={totalFilesCount}
        isAllSelected={isAllSelected}
        isRoot={isRoot}
        rootNodeId={rootNodeId}
        onSuccess={onSuccess}
        onSelect={onSelect}
        onToggleExpand={onToggleExpand}
        onSelectAll={onSelectAll}
        isFolderSelected={isFolderSelected}
        onToggleFolderSelection={onToggleFolderSelection}
        matchesSearch={matchesSearch}
        isTargeted={isTargeted || (isInternalDragging && isOver)}
        isDraggingActive={isDraggingActive}
        size={size}
      />
    </div>
  );
};

interface DraggableFileNodeItemProps {
  blob: BlobType;
  level: number;
  collectionTree: CollectionNode;
  onSuccess: () => void;
  onSelect: (e?: React.MouseEvent) => void;
  isSelected: boolean;
  matchesSearch: boolean;
  isTargeted: boolean;
  isDraggingActive: boolean;
  size: TreeViewSize;
  matterId?: string;
  disableDrag: boolean;
}

const DraggableFileNodeItem = ({
  blob,
  level,
  collectionTree,
  onSuccess,
  onSelect,
  isSelected,
  matchesSearch,
  isTargeted,
  isDraggingActive,
  size,
  matterId,
  disableDrag,
}: DraggableFileNodeItemProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: blob.id,
      disabled: disableDrag,
      data: {
        type: 'file',
        fileId: blob.id,
        fileParentId: blob.collection_details.id,
      },
    });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <FileNodeItem
        item={blob}
        level={level}
        collectionTree={collectionTree}
        onSuccess={onSuccess}
        onSelect={onSelect}
        isSelected={isSelected}
        matchesSearch={matchesSearch}
        isTargeted={isTargeted}
        isDraggingActive={isDraggingActive || isDragging}
        size={size}
        matterId={matterId}
      />
    </div>
  );
};

const DocumentDragPreview = ({
  blob,
  count,
}: {
  blob: BlobType;
  count: number;
}) => (
  <div className="px-2 py-1 rounded bg-blue-600 text-white shadow-lg flex items-center gap-1.5 text-xs font-medium whitespace-nowrap">
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
    {count > 1 ? `${count} files` : blob.name}
  </div>
);

interface RootDroppableContainerProps {
  rootNodeId: string;
  parentRef: React.MutableRefObject<HTMLDivElement | null>;
  className?: string;
  children: React.ReactNode;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}

const RootDroppableContainer = ({
  rootNodeId,
  parentRef,
  className,
  children,
  onDragOver,
  onDragLeave,
  onDrop,
}: RootDroppableContainerProps) => {
  const { setNodeRef } = useDroppable({
    id: rootNodeId,
    data: { type: 'folder', folderId: rootNodeId },
  });

  const handleRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    parentRef.current = node;
  };

  return (
    <div
      ref={handleRef}
      className={className}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-collection-id={rootNodeId}
    >
      {children}
    </div>
  );
};
